import React, { Fragment, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack } from '@mui/material';
import alertify from 'alertifyjs';
import Select from 'react-select-virtualized';

import { TraceFilterReferenceRangeDTO } from './models/TraceFilterReferenceRangeDTO';
import { useAPI } from 'hooks/useAPI';
import { ApiType } from 'models/enums/ApiType';
import { FetchType } from 'models/enums/FetchType';
import { useSeismicStore } from 'features/seismic/stores/useSeismicStore';
import { useMapStore } from 'features/seismic/stores/useMapStore';
import { calculateXPosition } from 'features/seismic/utils/calculateScreenValuesUtils';
import { Survey3DInfo } from 'features/seismic-3d/models/types/Survey3DInfo';

 interface TraceSortFilterSelector {
    sortOption:string;
    onSortChange: (sort: string) => void;
 }

export const TraceSortFilterSelector = ({sortOption, onSortChange}: TraceSortFilterSelector) => {
    const [itensToFilter, setItensToFilter] = useState<TraceFilterReferenceRangeDTO[]>([]);
    const [referenceToFilter, setReferenceToFilter] = useState<string>('-');

    const { map } = useMapStore(state => ({
        map: state.map
    }));

    const { setTraceRenderLimits, isInverted, scale, surveyMetadata, seismicCalculator } = useSeismicStore(state => ({
        setTraceRenderLimits: state.setTraceRenderLimits,
        isInverted: state.isInverted,
        scale: state.scale,
        surveyMetadata: state.surveyMetadata,
        seismicCalculator: state.calculator,
    }));

    const [ minTrace, maxTrace, increment ] = useMemo(() =>
        !!seismicCalculator && !!surveyMetadata ?
            [ seismicCalculator.getMinTrace(surveyMetadata), seismicCalculator.getMaxTrace(surveyMetadata), seismicCalculator.getLineIncrement(surveyMetadata) ]
            :
            [1,1,1]
    , [seismicCalculator, surveyMetadata]);

    const volumeToken = useMemo(() => surveyMetadata?.VolumeToken, [surveyMetadata]);

    const urlApi ='/Seismic/Volume/TracesIndexesBySortOption';

    const { execute: callApi, isLoading: isLoading } = useAPI<TraceFilterReferenceRangeDTO[]>(urlApi, (error) => {
        if (error.message.indexOf('Request failed with status code 401') > -1){
            alertify.alert('Authentication error');
        }
        else if (error.message.indexOf('Network Error') > -1){
            alertify.alert('Network error');
        }
        else {
            alertify.alert('An error ocurred');
        }
    });

    const filterOptions = useMemo(() => {
        return [ { value: '-', label: 'No filter' }, ...itensToFilter.map(item => ({ value: item.Reference, label: item.Reference }))];
    }, [itensToFilter]);

    const selectedFilterOption = useMemo(() => (filterOptions.find(option => option.value === referenceToFilter)), [filterOptions, referenceToFilter]);

    const {current: sortOptions} = useRef([
        { value: 'trace_index', label: 'Trace Index' },
        { value: 'shotpoint,channel_number', label: 'Shot + Channel Number' },
        { value: 'cdp,channel_number', label: 'CDP + Channel Number' },
        { value: 'channel_number,cdp', label: 'Channel Number + CDP' },
    ]);

    const selectedSortOption = useMemo(() => sortOptions.find(opt => sortOption === opt.value), [sortOption, sortOptions]);

    useEffect(() => {
        if (referenceToFilter !== '-') {
            console.log('changed:' + referenceToFilter);
            const selectedRef = itensToFilter.filter(x => x.Reference === referenceToFilter);
            const totalTraces = Math.floor((maxTrace - minTrace) / increment);

            if (selectedRef && selectedRef.length > 0) {
                setTraceRenderLimits([[selectedRef[0].TraceStart, selectedRef[0].TraceEnd]]);
            }
        }
        else {
            setTraceRenderLimits([[minTrace, maxTrace]]);
        }

    },[referenceToFilter, minTrace, maxTrace]);

    useEffect(() => {
        if (sortOption === 'trace_index') {
            setReferenceToFilter('-');
        }
    }, [sortOption]);

    useEffect(() => {
        if (!!volumeToken && !!sortOption && sortOption !== 'trace_index') {
            const fetch = async () => {
                const referenceList = await callApi({
                    apiType: ApiType.SeismicApi,
                    params:{
                        volumeToken: volumeToken,
                        sortOption: sortOption
                    },
                    type: FetchType.Get
                },
                );

                setItensToFilter(referenceList ?? []);
            };
            fetch();
        }
    },[sortOption, volumeToken]);

    return (
        <Fragment>
            <Stack gap={2} direction={'row'} padding={'0 15px'} sx={{color: 'black'}}>
                <Box width='205px'>
                    <Select isClearable={false} options={sortOptions} value={selectedSortOption} onChange={(val: { label: string, value: string }) => onSortChange(val.value)}/>
                </Box>
                {
                    sortOption && sortOption !== 'trace_index' ?
                        <Box>
                            <Select isClearable={false} options={filterOptions} value={selectedFilterOption} onChange={(newValue: { label: string, value: string }) => setReferenceToFilter(newValue.value)}/>
                        </Box>
                        : null
                }
            </Stack>
        </Fragment>
    );
};